import { useMemo } from 'react';
import { useBackendAITrading } from '../contexts/BackendAITradingContext';
import { useVidyaHeart, HeartReading } from './useVidyaHeart';
import { DEFAULT_HEART_PARAMS } from '../services/vidyaHeart';

/**
 * The timeframe the heart is read on when no strategy is trading it.
 *
 * A one-minute heart repaints often enough to be noise to someone who is only glancing at
 * the gauge, so an idle chart gets the hourly one.
 */
export const IDLE_HEART_INTERVAL = '1h';

export interface DisplayHeart extends HeartReading {
  /** True when the reading is following a saved strategy rather than the idle default. */
  followingStrategy: boolean;
}

/**
 * The heart as it should be *shown* for a symbol.
 *
 * Two callers need this and they must not disagree: the sentiment gauge in the right panel
 * and the marker on the chart. Whether the AI is running decides where the settings come
 * from — a strategy that has been saved and started is trading a particular interval and set
 * of bands, and the display follows it exactly — so the rule lives here once rather than
 * being written out at each call site.
 */
export function useDisplayHeart(symbol: string | undefined): DisplayHeart {
  const { getAssetAIConfig } = useBackendAITrading();

  const { config, followingStrategy } = useMemo(() => {
    const instance = symbol ? getAssetAIConfig(symbol)?.activeStrategyInstance : null;
    // Saved is not enough: the strategy has to have been started. `isActive` and
    // `aiTradeStartEnabled` are the same pair the trading engine reads.
    const running = Boolean(instance?.config && (instance.isActive || instance.aiTradeStartEnabled));

    return running
      ? { config: instance.config, followingStrategy: true }
      : {
          config: { initialEntryLevel: IDLE_HEART_INTERVAL, ...DEFAULT_HEART_PARAMS },
          followingStrategy: false
        };
  }, [symbol, getAssetAIConfig]);

  const reading = useVidyaHeart(symbol, config);

  return useMemo(() => ({ ...reading, followingStrategy }), [reading, followingStrategy]);
}

export default useDisplayHeart;
